import { useEffect, useState } from "react";
import { AxiosError } from "axios";

import { createToken, getBooking, getConfig } from "../api";
import Spinner from "../components/common/Spinner";
import { ReservationInfoResponse } from "../types/reservation";
import DueInPage from "./DueInPage";
import { RESERVATION_STATUS, STORAGE_KEY } from "../constants";
import CheckInPage from "./CheckInPage";
import Notification from "../components/Notification";
import { ConfigResponse } from "../types";
import { useTranslation } from "react-i18next";

const { RR, CI, CO } = RESERVATION_STATUS;

// booking 더미 데이터
// const reservationInfo = {
//   pms_code: "0000",
//   pms_message: "정상 처리되었습니다.",
//   hotel_name: "SANHA Hotel 11-11",
//   reservation_no: "25023770",
//   reservation_status: "RR",
//   check_in_date: "20250716",
//   check_out_date: "20250718",
//   guest_name: "H2O",
//   room_no: "0504",
//   nights: 1,
//   key_info: {
//     pincode: "127936",
//     exchange_key: "p2lg",
//     qr_data:
//       "73BDA3CE788B0CBFE521100B19DC17879E686F5BCE3CB7D59AB0E28DA3DF4BA6D950FF7D1C77190584F630296CE3ECEC1644BF6CD5124828C45992F182B635BED10B5E24F34E2F139F3687A7C2BF827C588F54BD804764B240FCB81B464F5A59",
//   },
// };

const HomePage = () => {
  const [config, setConfig] = useState<ConfigResponse | null>(null);
  const [reservationInfo, setReservationInfo] = useState<ReservationInfoResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { t } = useTranslation();

  const handleCreateToken = async () => {
    const queryParams = new URLSearchParams(window.location.search);
    const hotelCode = encodeURIComponent(queryParams.get("h") ?? "");
    const reservationNo = encodeURIComponent(queryParams.get("r") ?? "");

    if (!hotelCode || !reservationNo) {
      throw new Error(t("not_found_info"));
    }

    const {
      data: { access_token }
    } = await createToken({
      enc_hotel_code: hotelCode,
      enc_reservation_no: reservationNo
    });
    localStorage.setItem(STORAGE_KEY.TOKEN, access_token);
    localStorage.setItem(STORAGE_KEY.PARAMS, JSON.stringify({ hotelCode, reservationNo }));
  };

  const handleFetchData = async () => {
    const [configResponse, reservationResponse] = await Promise.all([getConfig(), getBooking()]);
    const { customer_service_number, select_room_yn, hotel_image, hotel_id } = configResponse.data;
    const { room_no } = reservationResponse.data;

    setConfig(configResponse.data);
    setReservationInfo(reservationResponse.data);

    localStorage.setItem(
      STORAGE_KEY.CONFIG,
      JSON.stringify({
        serviceNumber: customer_service_number,
        roomSelectYn: select_room_yn && !room_no,
        hotelImage: hotel_image,
        hotelId: hotel_id
      })
    );
    localStorage.setItem(STORAGE_KEY.ROOM_NO, room_no);
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        await handleCreateToken();
        await handleFetchData();
      } catch (error) {
        if (error instanceof AxiosError) {
          setErrorMessage(error.response?.data.message);
        } else if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage("Error Occurred");
        }
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  if (isLoading) return <Spinner />;
  if (isError || !reservationInfo || !config) return <Notification message={errorMessage} />;

  const renderContent = () => {
    const { reservation_status, reservation_no } = reservationInfo;
    switch (reservation_status) {
      case RR:
        return <DueInPage config={config} reservationInfo={reservationInfo} />;
      case CI:
        return <CheckInPage config={config} reservationInfo={reservationInfo} />;
      case CO:
        return <Notification message={t("already_checkout")} isBookingButton />;
      default:
        return <Notification message={`${t("check_reservation_status")} ${t("reservation_no")}: ${reservation_no}`} />;
    }
  };

  return renderContent();
};

export default HomePage;
